import "rsuite/dist/rsuite.min.css";
import { useAppDispatch, useAppSelector } from "state/hooks";
import { getGroupState, setGroup, setInitState } from "state/slices/group-slice";
import { useEffect } from "react";
import { CURRENT_GROUP_ID, IDLE, SUCCEEDED } from "utils/constants";
import {
  fetchAvailableGroups,
  getAvailableGroups,
  getAvailableGroupsStatus,
} from "state/slices/available-groups-slice";
import { useAuth } from "hooks/auth";
import { getRequest } from "utils/httpClient";
import * as storage from "utils/local-storage";
import { getGroupDetailsPath } from "utils/backend-path-builders";

const InitializeGroupState = (path: string) => {
  const isSlugUrl = path.includes("/g/");
  const dispatch = useAppDispatch();
  const currentGroupState = useAppSelector(getGroupState);
  const availableGroupsStatus = useAppSelector(getAvailableGroupsStatus);
  const availableGroups = useAppSelector(getAvailableGroups);
  const auth = useAuth();

  useEffect(() => {
    // If slug URL, let SetGroupStateFromSlug handle initialization
    if (isSlugUrl) return;

    // Anonymous users: let second useEffect handle initialization after checking local storage
    if (!auth.signedIn) return;

    if (availableGroupsStatus === IDLE) {
      // Only set "initializing" if not already "ready" — avoids overwriting
      // state set by the second useEffect (which may resolve before auth)
      if (currentGroupState.initState !== "ready") {
        dispatch(setInitState("initializing"));
      }
      dispatch(fetchAvailableGroups());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableGroupsStatus, dispatch, auth.signedIn, isSlugUrl]);

  // 1. Look for local storage group in available groups. If found, set as current group.
  // 2. If #1 not found, fetch group data for local storage group id. If found, set as current group,
  // otherwise just set current group as first available group (this might never happen?).
  useEffect(() => {
    if (currentGroupState.id !== "" || isSlugUrl) {
      // Group already set — ensure initState is "ready" (fixes race where
      // first useEffect overwrites "ready" with "initializing" after auth resolves)
      if (currentGroupState.initState !== "ready") {
        dispatch(setInitState("ready"));
      }
      return;
    }

    // Wait for available groups to be fetched before proceeding (signed-in users only)
    // Anonymous users skip this since fetchAvailableGroups is never called for them
    if (auth.signedIn && availableGroupsStatus !== SUCCEEDED) {
      return;
    }

    const localStorageGroupId = storage.local.getItem(CURRENT_GROUP_ID);

    const currentGroup = availableGroups.find((availableGroup) => {
      return localStorageGroupId === availableGroup.id;
    });

    if (currentGroup) {
      dispatch(setGroup(currentGroup));
      dispatch(setInitState("ready"));
    } else if (localStorageGroupId) {
      const path = getGroupDetailsPath(localStorageGroupId);
      getRequest(path, { skipIntegrationApi: true })
        .then((data) => {
          if (data) {
            dispatch(setGroup(data));
            dispatch(setInitState("ready"));
          } else {
            throw new Error(`Group with id ${localStorageGroupId} not found`);
          }
        })
        .catch(() => {
          storage.local.removeItem(CURRENT_GROUP_ID);
          window.location.reload();
        });
    } else if (availableGroups.length !== 0) {
      dispatch(setGroup(availableGroups[0]));
      dispatch(setInitState("ready"));
    } else {
      // No groups available for this user
      dispatch(setInitState("ready"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroupState.id, dispatch, isSlugUrl, availableGroups, availableGroupsStatus, auth.signedIn]);
};

export default InitializeGroupState;
